import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useAtom} from "jotai";
import {useLocation, useNavigate} from "react-router-dom";
import styled from "styled-components";

import {updateNoticeAdmin} from "../../services/notice/NoticeAdminAxios";
import {tokenResultAtom} from "../login/entity/Common";
import PlatformSectionHeader from "../platform_manage/PlatformSectionHeader";

export default function NoticeDetail() {
  const [tokenUserInfo] = useAtom(tokenResultAtom);
  const {state} = useLocation();
  const navigate = useNavigate();
  const {handleSubmit} = useForm();
  const [notice, setNotice] = useState({
    title: "",
    content: "",
    publishYn: "N"
  });

  const isAdmin = tokenUserInfo.role !== "NORMAL";

  useEffect(() => {
    if (state?.data) {
      setNotice(state.data);
    }
  }, [state]);

  const handlePublish = publishYn => {
    setNotice(current => ({...current, publishYn}));
  };

  const onSubmit = () => {
    if (!isAdmin) {
      return;
    }

    updateNoticeAdmin(notice).then(() =>
        navigate("/board/notice", {replace: true})
    );
  };

  return (
      <NoticeDetailForm onSubmit={handleSubmit(onSubmit)}>
        <NoticeDetailCard>
          <PlatformSectionHeader
              title="공지사항 상세"
              description="공지사항 제목과 내용을 확인하고 필요한 정보를 수정합니다."
          />

          <NoticeDetailBody>
            {isAdmin && (
                <FieldGroup>
                  <FieldLabel>게시 여부</FieldLabel>
                  <FieldDescription>
                    사용자에게 공지사항을 공개할지 선택합니다.
                  </FieldDescription>
                  <PublishToggle role="radiogroup" aria-label="게시 여부">
                    <PublishOption $active={notice.publishYn === "Y"}>
                      <input
                          type="radio"
                          name="notice-publish"
                          value="Y"
                          checked={notice.publishYn === "Y"}
                          onChange={() => handlePublish("Y")}
                      />
                      <StatusDot />
                      공개
                    </PublishOption>
                    <PublishOption $active={notice.publishYn === "N"} $inactive>
                      <input
                          type="radio"
                          name="notice-publish"
                          value="N"
                          checked={notice.publishYn === "N"}
                          onChange={() => handlePublish("N")}
                      />
                      <StatusDot />
                      비공개
                    </PublishOption>
                  </PublishToggle>
                </FieldGroup>
            )}

            <FieldGroup>
              <FieldLabel>
                공지사항 제목 {isAdmin && <RequiredMark>*</RequiredMark>}
              </FieldLabel>
              <FieldDescription>
                공지사항 목록과 상세 화면에 표시되는 제목입니다.
              </FieldDescription>
              <NoticeTitleInput
                  type="text"
                  placeholder="공지사항 제목을 입력해주세요."
                  value={notice.title ?? ""}
                  readOnly={!isAdmin}
                  onChange={event => setNotice(current => ({
                    ...current,
                    title: event.target.value
                  }))}
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>
                공지사항 내용 {isAdmin && <RequiredMark>*</RequiredMark>}
              </FieldLabel>
              <FieldDescription>
                사용자에게 안내할 공지사항의 전체 내용을 입력합니다.
              </FieldDescription>
              <NoticeContentEditor
                  placeholder="공지사항 내용을 입력해주세요."
                  value={notice.content ?? ""}
                  readOnly={!isAdmin}
                  onChange={event => setNotice(current => ({
                    ...current,
                    content: event.target.value
                  }))}
              />
            </FieldGroup>
          </NoticeDetailBody>

        </NoticeDetailCard>

        <NoticeActions>
          <NoticeCancelButton type="button" onClick={() => navigate(-1)}>
            목록
          </NoticeCancelButton>
          {isAdmin && (
              <NoticeSubmitButton type="submit">
                정보 수정
              </NoticeSubmitButton>
          )}
        </NoticeActions>
      </NoticeDetailForm>
  );
}

const NoticeDetailForm = styled.form`
  width: 100%;
`;

const NoticeDetailCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;

const NoticeDetailBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const FieldGroup = styled.section`
  padding: 18px;
  border: 1px solid #e8ebee;
  border-radius: 9px;
  background: #fafbfc;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  color: #333;
  font-size: var(--font-size-14);
  font-weight: 700;
`;

const RequiredMark = styled.span`
  color: #f5811f;
`;

const FieldDescription = styled.p`
  margin: 0 0 12px;
  color: #8a9299;
  font-size: var(--font-size-11);
  line-height: 1.5;
`;

const inputFocusStyle = `
  &:hover:not(:read-only) {
    border-color: #f1d4c2;
  }

  &:focus:not(:read-only) {
    border-color: #f5811f;
    outline: 0;
    box-shadow: 0 0 0 3px rgba(245, 129, 31, 0.13);
  }

  &:read-only {
    background: #f5f6f7;
    color: #555;
    cursor: default;
  }
`;

const NoticeTitleInput = styled.input`
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid #dfe3e6;
  border-radius: 8px;
  background: #fff;
  color: #333;
  font-size: var(--font-size-13);
  ${inputFocusStyle}
`;

const NoticeContentEditor = styled.textarea`
  width: 100%;
  min-height: 680px;
  max-height: 820px;
  box-sizing: border-box;
  overflow-y: auto;
  padding: 18px;
  resize: vertical;
  border: 1px solid #dfe3e6;
  border-radius: 8px;
  background: #fff;
  color: #444;
  font-family: inherit;
  font-size: var(--font-size-13);
  line-height: 1.75;
  white-space: pre-wrap;
  ${inputFocusStyle}
`;

const PublishToggle = styled.div`
  display: inline-flex;
  gap: 6px;
  padding: 4px;
  border: 1px solid #e1e5e8;
  border-radius: 9px;
  background: #f5f6f7;
`;

const PublishOption = styled.label`
  position: relative;
  display: inline-flex;
  min-width: 86px;
  height: 30px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 12px;
  border: 1px solid ${({$active, $inactive}) =>
      $active ? ($inactive ? "#e5caca" : "#c9e4d4") : "transparent"};
  border-radius: 6px;
  background: ${({$active, $inactive}) =>
      $active ? ($inactive ? "#fff3f3" : "#eef9f2") : "transparent"};
  color: ${({$active, $inactive}) =>
      $active ? ($inactive ? "#b84a4a" : "#318553") : "#7b8289"};
  font-size: var(--font-size-12);
  font-weight: 700;
  cursor: pointer;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
  }
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const NoticeActions = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
  padding: 14px 20px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -4px 18px rgba(31, 41, 55, 0.05);

  button {
    width: auto;
    min-width: 92px;
    height: 38px;
    margin: 0;
    padding: 0 18px;
    border-radius: 6px;
    font-size: var(--font-size-13);
    font-weight: 700;
  }
`;

const NoticeCancelButton = styled.button`
  min-width: 74px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  font-size: var(--font-size-13);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #f1d4c2;
    background: #fffaf6;
    color: #f5811f;
  }
`;

const NoticeSubmitButton = styled.button`
  min-width: 92px;
  height: 40px;
  padding: 0 17px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-13);
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #ffe8d2;
  }
`;
